"use client";

import { Label, Pie, PieChart } from "recharts";

import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../../../third-party/shadcn/card";

import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../../../third-party/shadcn/chart";
import { IntegrationItem } from "../../../../models/integration-item.model";
import { useEffect, useState } from "react";
import { chartColors } from "../../../../constants/design.constants";

interface Props {
  data: IntegrationItem[];
}

const chartConfig = {
  attempts: {
    label: "Attempts",
  },
  Success: {
    label: "Success",
    color: chartColors.success,
  },
  Failed: {
    label: "Failed",
    color: chartColors.danger,
  },
} satisfies ChartConfig;

export const IntegrationPieChart: React.FC<Props> = (props: Props) => {
  const { data } = props;
  const [chartProps, setChartProps] = useState<
    { result: string; attempts: number; fill: string }[]
  >([]);

  const init = () => {
    const sluChartData = [
      {
        result: "Success",
        attempts: data.filter((x) => x.conclusion !== "failure").length,
        fill: chartColors.success,
      },
      {
        result: "Failed",
        attempts: data.filter((x) => x.conclusion === "failure").length,
        fill: chartColors.danger,
      },
    ];
    setChartProps(sluChartData);
  };
  useEffect(() => {
    init();
  }, [data]);

  const successfulRuns = data.filter((x) => x.conclusion === "success").length;
  const totalRuns = data.filter((x) => x.conclusion).length;

  return !chartProps ? (
    <>Loading...</>
  ) : (
    <>
      <CardHeader className="items-center pb-0">
        <CardTitle>Integration Runs</CardTitle>
        <CardDescription>
          01 {new Date().toLocaleString("default", { month: "long" })} — current
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square max-h-[250px]"
        >
          <PieChart>
            <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
            <Pie
              data={chartProps}
              dataKey="attempts"
              nameKey="result"
              innerRadius={55}
              strokeWidth={2}
              stroke="hsl(var(--card))"
            >
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className="fill-foreground text-3xl font-medium"
                          style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                        >
                          {totalRuns.toLocaleString()}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 22}
                          className="fill-muted-foreground text-[10px] uppercase tracking-widest"
                        >
                          Runs
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </Pie>
          </PieChart>
        </ChartContainer>
        <div className="flex flex-row justify-evenly pb-4 pt-2">
          <div className="flex flex-row items-center gap-2">
            <div
              className="h-2 w-2"
              style={{ backgroundColor: chartColors.success }}
            />
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Success
            </div>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div
              className="h-2 w-2"
              style={{ backgroundColor: chartColors.danger }}
            />
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Failed
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        <div className="flex items-center gap-2 font-medium leading-none text-center font-mono text-signal">
          {totalRuns
            ? `${Math.floor((successfulRuns / totalRuns) * 100)}% success`
            : "—"}
        </div>
        <div className="leading-relaxed text-muted-foreground text-center text-xs">
          Integration runs for this month to date
        </div>
      </CardFooter>
    </>
  );
};
